import { useEffect, useState, type ReactNode } from "react";
import { Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./auth/AuthProvider";
import { captureLocation } from "./lib/geo";
import { setSentryUser } from "./lib/sentry";
import type { MatchDoc } from "./lib/types";
import { ErrorBoundary } from "./components/ErrorBoundary";
import PullToRefresh from "./components/PullToRefresh";
import SignIn from "./screens/SignIn";
import ProfileSetup from "./screens/ProfileSetup";
import Discover from "./screens/Discover";
import Matches from "./screens/Matches";
import Chat from "./screens/Chat";
import Relay from "./screens/Relay";
import RelayGroupQueue from "./screens/RelayGroupQueue";
import RelayRoom from "./screens/RelayRoom";
import DuelQueue from "./screens/DuelQueue";
import DuelRoom from "./screens/DuelRoom";
import Profile from "./screens/Profile";
import AccountSettings from "./screens/AccountSettings";

export default function App() {
  const { phase, uid } = useAuth();

  useEffect(() => setSentryUser(uid), [uid]);

  if (phase === "loading") {
    return (
      <div className="splash">
        <div className="spinner" />
      </div>
    );
  }
  // SignIn/ProfileSetup live outside the app shell, and the document doesn't
  // scroll (html/body are overflow:hidden) — give them their own scroller.
  if (phase === "signedOut") {
    return <div className="page-scroll"><ErrorBoundary area="signin"><SignIn /></ErrorBoundary></div>;
  }
  if (phase === "needsProfile") {
    return <div className="page-scroll"><ErrorBoundary area="setup"><ProfileSetup /></ErrorBoundary></div>;
  }
  return <AppShell />;
}

function AppShell() {
  const location = useLocation();
  const { uid } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [offline, setOffline] = useState(!navigator.onLine);

  // Unread-conversations badge on the Matches tab (iOS MatchesBadge).
  useEffect(() => {
    if (!uid) return;
    const q = query(collection(db, "matches"), where("participants", "array-contains", uid));
    return onSnapshot(q, (snap) => {
      let n = 0;
      snap.docs.forEach((d) => {
        const m = d.data() as MatchDoc;
        if (m.state === "expired") return;
        const lastMsg = m.lastMessageAt?.toMillis() ?? 0;
        const lastRead = m.lastRead?.[uid]?.toMillis() ?? 0;
        if (lastMsg > 0 && m.streak.uid && m.streak.uid !== uid && lastMsg > lastRead) n++;
      });
      setUnreadCount(n);
    });
  }, [uid]);

  // Reconnecting banner (iOS NetworkStatus capsule).
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // Silent location refresh on launch when permission is already granted
  // (iOS refreshes on every foreground; also feeds the lastActive heartbeat).
  useEffect(() => {
    if (!uid || !navigator.permissions?.query) return;
    void navigator.permissions.query({ name: "geolocation" as PermissionName })
      .then((p) => { if (p.state === "granted") void captureLocation(uid).catch(() => {}); })
      .catch(() => {});
  }, [uid]);
  // Full-screen surfaces hide the tab bar (chat + live relay room/duel).
  const immersive =
    location.pathname.startsWith("/chat/") ||
    location.pathname.startsWith("/relay/room/") ||
    location.pathname.startsWith("/relay/duel/");

  return (
    <div className="app-shell">
      <PullToRefresh>
        <ErrorBoundary area={location.pathname}>
          <Routes>
            {/* Relay is the front door — the differentiator opens first. */}
            <Route path="/relay" element={<Relay />} />
            <Route path="/relay/group" element={<RelayGroupQueue />} />
            <Route path="/relay/room/:roomId" element={<RelayRoom />} />
            <Route path="/relay/duel" element={<DuelQueue />} />
            <Route path="/relay/duel/:duelId" element={<DuelRoom />} />
            <Route path="/discover" element={<Discover />} />
            <Route path="/matches" element={<Matches />} />
            <Route path="/chat/:matchId" element={<Chat />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings" element={<AccountSettings />} />
            <Route path="*" element={<Navigate to="/relay" replace />} />
          </Routes>
        </ErrorBoundary>
      </PullToRefresh>
      {offline && (
        <div className="net-banner" role="status">
          <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
          Reconnecting…
        </div>
      )}
      {!immersive && (
        <nav className="tab-bar">
          <Tab to="/relay" label="Relay" icon={<TabRelayIcon />} />
          <Tab to="/discover" label="Discover" icon="✨" />
          <Tab to="/matches" label="Matches" icon="💬" badge={unreadCount} />
          <Tab to="/profile" label="Profile" icon="👤" />
        </nav>
      )}
    </div>
  );
}

/** Miniature dotted-triangle Relay mark for the tab bar (inherits tab color). */
function TabRelayIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 100 100" aria-hidden>
      <g fill="currentColor">
        <circle cx="28" cy="30" r="13" />
        <circle cx="72" cy="30" r="13" />
        <circle cx="50" cy="72" r="13" />
        <circle cx="50" cy="27" r="4.5" />
        <circle cx="37" cy="48" r="4.5" />
        <circle cx="63" cy="48" r="4.5" />
      </g>
    </svg>
  );
}

function Tab({ to, label, icon, badge = 0 }: { to: string; label: string; icon: ReactNode; badge?: number }) {
  return (
    <NavLink to={to} className={({ isActive }) => "tab" + (isActive ? " tab-active" : "")}>
      <span className="tab-icon" aria-hidden>{icon}</span>
      <span className="tab-label">{label}</span>
      {badge > 0 && <span className="tab-badge" aria-label={`${badge} unread`}>{badge > 9 ? "9+" : badge}</span>}
    </NavLink>
  );
}
